import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { getAllProviders } from '@/utils/aiProviders';
import { AIProvider } from '@/types/aiProvider';
import { 
  Wand2, 
  Lightbulb, 
  FileText, 
  Loader2,
  Copy,
  Check,
  Layout,
  PenTool,
  Bot,
  Target,
  MessageSquare,
  Link as LinkIcon,
  AlertTriangle,
  List,
  Save,
  Link2,
  Sparkles,
  HelpCircle
} from 'lucide-react';
import { SanitizedContent } from '@/components/ui/sanitized-content';
import { useArticleDraft } from '@/hooks/useArticleDraft';

interface Topic {
  title: string;
  description: string;
}

interface Framework {
  sections: Array<{
    heading: string;
    subheadings?: string[];
    keyPoints: string[];
    paragraphTopics: string[];
  }>;
  keyTakeaways: string[];
  callToAction: string[];
  riskWarnings?: string[];
}

interface AIArticleBuilderProps {
  onArticleGenerated: (data: {
    title: string;
    content: string;
    excerpt?: string;
    meta_description?: string;
    focus_keyword?: string;
    og_title?: string;
    og_description?: string;
  }) => void;
  onGeneratingChange?: (isGenerating: boolean) => void;
  initialTopic?: {
    title: string;
    description: string;
    keywords: string[];
  };
}

export const AIArticleBuilder: React.FC<AIArticleBuilderProps> = ({ 
  onArticleGenerated, 
  onGeneratingChange,
  initialTopic 
}) => {
  const [prompt, setPrompt] = useState(initialTopic ? 
    `Write an article about: ${initialTopic.title}\n\nDescription: ${initialTopic.description}\n\nKeywords: ${initialTopic.keywords.join(', ')}` 
    : ''
  );
  const [provider, setProvider] = useState<AIProvider>('openai');
  const [step, setStep] = useState(initialTopic ? 1 : 1);
  const [loading, setLoading] = useState(false);
  const { hasDraft, saveDraft, loadDraft, clearDraft } = useArticleDraft();
  
  // Generated data
  const [topics, setTopics] = useState<Topic[]>([]);
  const [selectedTopic, setSelectedTopic] = useState<Topic | null>(null);
  const [contentType, setContentType] = useState<'article' | 'framework' | null>(null);
  const [wordCount, setWordCount] = useState(800);
  const [tone, setTone] = useState('professional');
  const [includeKeyTakeaways, setIncludeKeyTakeaways] = useState(false);
  const [includeCallToAction, setIncludeCallToAction] = useState(false);
  const [includeSources, setIncludeSources] = useState(false);
  const [includeRiskWarnings, setIncludeRiskWarnings] = useState(false);
  const [includeTableOfContents, setIncludeTableOfContents] = useState(true);
  const [includeFAQ, setIncludeFAQ] = useState(false);
  const [generatedContent, setGeneratedContent] = useState('');
  const [framework, setFramework] = useState<Framework | null>(null);
  const [copiedItems, setCopiedItems] = useState<Set<string>>(new Set());
  
  // Link preview state
  const [linkPreviewData, setLinkPreviewData] = useState<any>(null);
  const [showLinkPreview, setShowLinkPreview] = useState(false);
  const [linkingInProgress, setLinkingInProgress] = useState(false);
  const [linkedContent, setLinkedContent] = useState<string | null>(null);

  const providers = getAllProviders();

  // Auto-save draft when key fields change
  React.useEffect(() => {
    if (prompt || selectedTopic || generatedContent) {
      const timer = setTimeout(() => {
        saveDraft({
          prompt,
          provider,
          wordCount,
          tone,
          selectedTopic: selectedTopic || undefined,
          generatedContent: generatedContent || undefined,
          includeKeyTakeaways: includeKeyTakeaways ? 'yes' : 'no',
          includeCallToAction: includeCallToAction ? 'yes' : 'no',
          includeSources: includeSources ? 'yes' : 'no',
          includeRiskWarnings: includeRiskWarnings ? 'yes' : 'no',
          includeTableOfContents: includeTableOfContents ? 'yes' : 'no'
        });
      }, 2000); // Auto-save after 2 seconds of inactivity

      return () => clearTimeout(timer);
    }
  }, [prompt, selectedTopic, generatedContent, provider, wordCount, tone, includeKeyTakeaways, includeCallToAction, includeSources, includeRiskWarnings, includeTableOfContents]);

  // Load draft on mount
  React.useEffect(() => {
    if (hasDraft) {
      const draft = loadDraft();
      if (draft) {
        toast.info('Draft loaded from previous session', {
          action: {
            label: 'Clear',
            onClick: () => {
              clearDraft();
              resetBuilder();
            }
          }
        });
        setPrompt(draft.prompt);
        setProvider(draft.provider as AIProvider);
        setWordCount(draft.wordCount);
        setTone(draft.tone);
        setIncludeKeyTakeaways(draft.includeKeyTakeaways === 'yes');
        setIncludeCallToAction(draft.includeCallToAction === 'yes');
        setIncludeSources(draft.includeSources === 'yes');
        setIncludeRiskWarnings(draft.includeRiskWarnings === 'yes');
        setIncludeTableOfContents(draft.includeTableOfContents === 'yes');
        if (draft.selectedTopic) {
          setSelectedTopic(draft.selectedTopic);
        }
        if (draft.generatedContent) {
          setGeneratedContent(draft.generatedContent);
        }
      }
    }
  }, []);

  const callAIFunction = async (type: string, data: any) => {
    const { data: result, error } = await supabase.functions.invoke('ai-article-generator', {
      body: { provider, type, ...data }
    });

    if (error) throw error;
    if (!result.success) throw new Error(result.error);
    
    return result.data;
  };

  const generateTopics = async () => {
    if (!prompt.trim()) {
      toast.error('Please enter a topic prompt');
      return;
    }

    setLoading(true);
    try {
      // If we have an initial topic, skip topic generation and go straight to article
      if (initialTopic) {
        const topic: Topic = {
          title: initialTopic.title,
          description: initialTopic.description
        };
        setSelectedTopic(topic);
        setStep(3); // Skip to content type selection
        toast.success('Topic loaded! Now select your content type.');
      } else {
        const topicsData = await callAIFunction('topics', { prompt });
        setTopics(Array.isArray(topicsData) ? topicsData : []);
        setStep(2);
        toast.success('Topics generated successfully!');
      }
    } catch (error) {
      console.error('Error generating topics:', error);
      toast.error('Failed to generate topics. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const selectTopic = (topic: Topic) => {
    setSelectedTopic(topic);
    setStep(3);
  };

  const selectContentType = (type: 'article' | 'framework') => {
    setContentType(type);
    if (type === 'article') {
      setStep(4); // Go to word count/tone selection
    } else {
      generateFramework(); // Generate framework immediately
    }
  };

  const generateFramework = async () => {
    if (!selectedTopic) return;
    
    setLoading(true);
    try {
      const frameworkData = await callAIFunction('framework', { 
        prompt: selectedTopic.title 
      });
      setFramework(frameworkData);
      setStep(5);
      toast.success('Framework generated successfully!');
    } catch (error) {
      console.error('Error generating framework:', error);
      toast.error('Failed to generate framework. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const generateArticle = async () => {
    if (!selectedTopic) return;

    setLoading(true);
    try {
      const articleData = await callAIFunction('article', { 
        prompt: selectedTopic.title, 
        wordCount, 
        tone,
        includeKeyTakeaways: includeKeyTakeaways ? 'yes' : 'no',
        includeCallToAction: includeCallToAction ? 'yes' : 'no',
        includeSources: includeSources ? 'yes' : 'no',
        includeRiskWarnings: includeRiskWarnings ? 'yes' : 'no',
        includeTableOfContents: includeTableOfContents ? 'yes' : 'no',
        includeFAQ: includeFAQ ? 'yes' : 'no'
      });
      
      // ✅ CRITICAL FIX: Automatically insert real links during article generation
      // This ensures all URLs are real, not placeholders
      try {
        console.log('🔗 Auto-linking keywords in generated article...');
        const { data: linkResult } = await supabase.functions.invoke('link-article-keywords', {
          body: { 
            content: articleData.content,
            currentArticleSlug: '',
            previewOnly: false
          }
        });

        if (linkResult?.linkedContent) {
          console.log(`✅ Auto-linked with ${linkResult.stats?.totalLinksCreated || 0} real URLs`);
          setGeneratedContent(linkResult.linkedContent);
          setLinkedContent(linkResult.linkedContent);
        } else {
          console.warn('⚠️ Link function returned no content, using original');
          setGeneratedContent(articleData.content);
          setLinkedContent(null);
        }
      } catch (linkError) {
        console.error('❌ Auto-linking failed:', linkError);
        // Continue with unlinked content
        setGeneratedContent(articleData.content);
        setLinkedContent(null);
      }
      
      setStep(6);
      
      // Track API usage for cost estimation
      if ((window as any).trackAIUsage) {
        (window as any).trackAIUsage(provider, wordCount);
      }
      
      toast.success('Article generated with real links!');
    } catch (error) {
      console.error('Error generating article:', error);
      toast.error('Failed to generate article. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const useGeneratedContent = async () => {
    if (!selectedTopic) return;

    const card = document.querySelector('.ai-article-builder-card');
    if (card) {
      card.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    if (contentType === 'article' && generatedContent) {
      // ✅ Use linkedContent if available, otherwise use generatedContent
      const contentToUse = linkedContent || generatedContent;
      
      // Only strip code fences and normalize whitespace - preserve all HTML attributes
      // CRITICAL: Also remove literal \n strings that AI may have included
      let cleanContent = contentToUse
        .replace(/```html|```/g, '')
        .replace(/\\n/g, '\n') // Convert escaped \n to actual newlines
        .replace(/>\s*\n\s*</g, '>\n<')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
      
      const plainText = cleanContent.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
      const excerpt = plainText.slice(0, 200) + (plainText.length > 200 ? '...' : '');

      // Check ACTUAL word count (plain text only, not HTML)
      const actualWordCount = plainText.split(/\s+/).filter(w => w.length > 0).length;
      const htmlSize = cleanContent.length;
      
      // Validate content before loading
      if (actualWordCount > 3000) {
        toast.error(`Content too long: ${actualWordCount} words (max 3000)`);
        return;
      }
      
      if (htmlSize > 100000) {
        toast.error(`Content too large: ${Math.round(htmlSize/1000)}KB (max 100KB)`);
        return;
      }
      
      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(cleanContent, 'text/html');
        const parseErrors = doc.querySelectorAll('parsererror');
        if (parseErrors.length > 0) {
          toast.error('Content contains invalid HTML');
          return;
        }
      } catch (e) {
        toast.error('Failed to parse content');
        return;
      }
      
      setStep(7);
      setLoading(true);
      onGeneratingChange?.(true);
      
      try {
        // Generate SEO data first
        toast.info('Generating SEO metadata...');
        const seoData = await callAIFunction('seo', { 
          prompt: selectedTopic.title 
        });
        
        // Generate 300-word excerpt with focus keyword
        toast.info('Generating article excerpt...');
        const excerptData = await callAIFunction('excerpt', {
          content: plainText,
          focusKeyword: seoData.focus_keyword,
          title: selectedTopic.title
        });
        
        // Only populate form ONCE with complete data to prevent multiple heavy renders
        toast.info('Loading article into editor...');
        
        onArticleGenerated({
          title: selectedTopic.title,
          content: cleanContent,
          excerpt: excerptData.excerpt,
          meta_description: seoData.meta_description,
          focus_keyword: seoData.focus_keyword,
          og_title: seoData.og_title,
          og_description: seoData.og_description
        });
        
        toast.success('Article ready! Click "Publish Now" or "Save Draft" at the bottom.');
        
      } catch (error) {
        console.error('Error:', error);
        // Fallback: populate without SEO if it fails
        const fallbackExcerpt = plainText.slice(0, 1800) + (plainText.length > 1800 ? '...' : '');
        onArticleGenerated({
          title: selectedTopic.title,
          content: cleanContent,
          excerpt: fallbackExcerpt
        });
        toast.warning('Article populated but excerpt/SEO generation failed.');
      } finally {
        setLoading(false);
        onGeneratingChange?.(false);
      }
    } else if (contentType === 'framework' && framework) {
      // Convert framework to basic content structure
      let content = `<h1>${selectedTopic.title}</h1>\n\n`;
      
      framework.sections.forEach(section => {
        content += `<h2>${section.heading}</h2>\n`;
        if (section.subheadings) {
          section.subheadings.forEach(sub => {
            content += `<h3>${sub}</h3>\n`;
          });
        }
        content += `<p>Key points to cover:</p>\n<ul>\n`;
        section.keyPoints.forEach(point => {
          content += `<li>${point}</li>\n`;
        });
        content += `</ul>\n\n`;
      });

      if (framework.keyTakeaways.length > 0) {
        content += `<h2>Key Takeaways</h2>\n<ul>\n`;
        framework.keyTakeaways.forEach(takeaway => {
          content += `<li>${takeaway}</li>\n`;
        });
        content += `</ul>\n\n`;
      }

      onArticleGenerated({
        title: selectedTopic.title,
        content,
        excerpt: selectedTopic.description
      });
    }

    toast.success('Content populated successfully!');
  };

  const handleAnalyzeLinks = async () => {
    if (!generatedContent) {
      toast.error('No content to analyze');
      return;
    }

    setLinkingInProgress(true);
    setShowLinkPreview(false);
    
    try {
      const { data, error } = await supabase.functions.invoke('link-article-keywords', {
        body: {
          content: generatedContent,
          currentArticleSlug: 'new-article',
          previewOnly: true,
          externalSources: []
        }
      });

      if (error) throw error;

      if (data?.preview) {
        setLinkPreviewData(data);
        setShowLinkPreview(true);
        toast.success(`Found ${data.stats?.totalLinksWillCreate || 0} potential links!`);
      } else {
        toast.info('No links found to add.');
      }
    } catch (error: any) {
      toast.error('Failed to analyze links');
    } finally {
      setLinkingInProgress(false);
    }
  };

  const handleApplyLinks = async () => {
    setLinkingInProgress(true);
    try {
      const { data, error } = await supabase.functions.invoke('link-article-keywords', {
        body: {
          content: generatedContent,
          currentArticleSlug: 'new-article',
          previewOnly: false,
          externalSources: []
        }
      });

      if (error) throw error;

      if (data?.linkedContent) {
        setLinkedContent(data.linkedContent);
        setShowLinkPreview(false);
        const linkCount = data.stats?.totalLinksCreated || 0;
        toast.success(`✓ Added ${linkCount} links! Ready to use.`);
      }
    } catch (error: any) {
      toast.error('Failed to add links');
    } finally {
      setLinkingInProgress(false);
    }
  };

  const handleSkipLinks = () => {
    setLinkedContent(null);
    setShowLinkPreview(false);
    setLinkPreviewData(null);
    toast.info('Skipped adding links');
  };

  const copyToClipboard = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedItems(prev => new Set([...prev, id]));
      setTimeout(() => {
        setCopiedItems(prev => {
          const newSet = new Set(prev);
          newSet.delete(id);
          return newSet;
        });
      }, 2000);
      toast.success('Copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const resetBuilder = () => {
    setStep(1);
    setPrompt('');
    setProvider('openai');
    setTopics([]);
    setSelectedTopic(null);
    setContentType(null);
    setGeneratedContent('');
    setFramework(null);
    setCopiedItems(new Set());
    setLinkedContent(null);
    setLinkPreviewData(null);
    setShowLinkPreview(false);
    setIncludeSources(false);
    setIncludeRiskWarnings(false);
    setIncludeFAQ(false);
    setLinkPreviewData(null);
    setShowLinkPreview(false);
    setLinkingInProgress(false);
    setLinkedContent(null);
  };

  return (
    <Card className="mb-6 ai-article-builder-card">
      <CardHeader className="bg-gradient-to-r from-primary/5 to-transparent border-b">
        <CardTitle className="flex items-center gap-2 text-primary">
          <Wand2 className="w-5 h-5" />
          AI Article Builder
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6 pt-6">
        {/* Step 1: Initial Prompt */}
        {step === 1 && (
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">
                What would you like to write about?
              </label>
              <Textarea
                placeholder="e.g., 'The future of artificial intelligence in healthcare' or 'Best practices for sustainable business growth'"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={3}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium mb-3 block">Choose AI Provider</label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {providers.map((p) => (
                  <button
                    key={p.name}
                    onClick={() => setProvider(p.name as AIProvider)}
                    className={`
                      relative p-4 rounded-lg border-2 text-left transition-all duration-200 hover-scale
                      ${provider === p.name 
                        ? 'border-primary bg-primary/5 shadow-md' 
                        : 'border-border hover:border-primary/50 hover:bg-accent'
                      }
                    `}
                  >
                    <div className="flex items-start gap-3">
                      <Bot className={`w-5 h-5 mt-0.5 ${provider === p.name ? 'text-primary' : 'text-muted-foreground'}`} />
                      <div className="flex-1 min-w-0">
                        <div className={`font-semibold text-sm ${provider === p.name ? 'text-primary' : 'text-foreground'}`}>
                          {p.displayName}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1 line-clamp-2">
                          {p.description}
                        </div>
                      </div>
                    </div>
                    {provider === p.name && (
                      <div className="absolute top-2 right-2">
                        <Check className="w-4 h-4 text-primary" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
            
            <Button onClick={generateTopics} disabled={loading} className="w-full">
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {initialTopic ? 'Preparing Article...' : 'Generating Topics...'}
                </>
              ) : (
                <>
                  <Lightbulb className="w-4 h-4 mr-2" />
                  {initialTopic ? 'Generate Article' : 'Generate Article Topics'}
                </>
              )}
            </Button>
          </div>
        )}

        {/* Step 2: Topic Selection */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-primary">Choose a Topic</h3>
              <Button variant="outline" size="sm" onClick={() => setStep(1)} className="border-primary/30 hover:bg-primary/10">
                Back
              </Button>
            </div>
            <div className="grid gap-3">
              {topics.map((topic, index) => (
                <Card 
                  key={index} 
                  className="p-4 cursor-pointer hover:bg-primary/5 hover:border-primary/40 transition-all duration-200 border-primary/20"
                  onClick={() => selectTopic(topic)}
                >
                  <h4 className="font-medium mb-2 text-primary">{topic.title}</h4>
                  <p className="text-sm text-muted-foreground">{topic.description}</p>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Step 3: Content Type Selection */}
        {step === 3 && selectedTopic && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-primary">How would you like to proceed?</h3>
              <Button variant="outline" size="sm" onClick={() => setStep(2)} className="border-primary/30 hover:bg-primary/10">
                Back
              </Button>
            </div>
            <p className="text-muted-foreground mb-4">
              Selected topic: <strong className="text-primary">{selectedTopic.title}</strong>
            </p>
            <div className="grid gap-4">
              <Card 
                className="p-6 cursor-pointer hover:bg-primary/5 transition-all duration-200 border-2 border-primary/20 hover:border-primary"
                onClick={() => selectContentType('article')}
              >
                <div className="flex items-start gap-4">
                  <FileText className="w-6 h-6 text-primary mt-1" />
                  <div>
                    <h4 className="font-semibold mb-2 text-primary">Generate Full Article</h4>
                    <p className="text-sm text-muted-foreground">
                      AI will write a complete article with your specified word count and tone. 
                      Perfect for when you want ready-to-publish content.
                    </p>
                  </div>
                </div>
              </Card>
              
              <Card 
                className="p-6 cursor-pointer hover:bg-primary/5 transition-all duration-200 border-2 border-primary/20 hover:border-primary"
                onClick={() => selectContentType('framework')}
              >
                <div className="flex items-start gap-4">
                  <Layout className="w-6 h-6 text-primary mt-1" />
                  <div>
                    <h4 className="font-semibold mb-2 text-primary">Generate Article Framework</h4>
                    <p className="text-sm text-muted-foreground">
                      Get a detailed structure with suggested sections, key points, takeaways, 
                      and CTAs. Perfect for guided writing with your own voice.
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        )}

        {/* Step 4: Article Settings (Word Count & Tone) */}
        {step === 4 && contentType === 'article' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-primary">Article Settings</h3>
              <Button variant="outline" size="sm" onClick={() => setStep(3)}>
                Back
              </Button>
            </div>
            
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
              <div>
              <label className="text-sm font-medium mb-2 flex items-center gap-2">
                <FileText className="w-4 h-4 text-primary" />
                Target Word Count
              </label>
                <Select value={wordCount.toString()} onValueChange={(value) => setWordCount(parseInt(value))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="500">500 words</SelectItem>
                    <SelectItem value="800">800 words</SelectItem>
                    <SelectItem value="1200">1,200 words</SelectItem>
                    <SelectItem value="1500">1,500 words</SelectItem>
                    <SelectItem value="2000">2,000 words</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 flex items-center gap-2">
                  <PenTool className="w-4 h-4 text-primary" />
                  Writing Tone
                </label>
                <Select value={tone} onValueChange={setTone}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="professional">Professional</SelectItem>
                    <SelectItem value="friendly">Friendly</SelectItem>
                    <SelectItem value="authoritative">Authoritative</SelectItem>
                    <SelectItem value="conversational">Conversational</SelectItem>
                    <SelectItem value="academic">Academic</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              </div>

              <div className="space-y-3 pt-4 border-t">
                <h4 className="text-sm font-semibold text-foreground mb-3">Content Options</h4>

                <div className="flex items-center justify-between p-3 rounded-lg border bg-card">
                <label className="text-sm font-medium flex items-center gap-2 cursor-pointer" htmlFor="key-takeaways">
                  <Target className="w-4 h-4 text-primary" />
                  Include Key Takeaways
                </label>
                <Switch 
                  id="key-takeaways"
                  checked={includeKeyTakeaways} 
                  onCheckedChange={setIncludeKeyTakeaways}
                />
                </div>

                <div className="flex items-center justify-between p-3 rounded-lg border bg-card">
                  <label className="text-sm font-medium flex items-center gap-2 cursor-pointer" htmlFor="call-to-action">
                  <MessageSquare className="w-4 h-4 text-primary" />
                  Include Call to Action
                </label>
                <Switch 
                  id="call-to-action"
                  checked={includeCallToAction} 
                  onCheckedChange={setIncludeCallToAction}
                />
                </div>

                <div className="flex items-center justify-between p-3 rounded-lg border bg-card">
                  <label className="text-sm font-medium flex items-center gap-2 cursor-pointer" htmlFor="sources">
                  <LinkIcon className="w-4 h-4 text-primary" />
                  Include Sources & URLs
                </label>
                <Switch 
                  id="sources"
                  checked={includeSources} 
                  onCheckedChange={setIncludeSources}
                />
                </div>

                <div className="flex items-center justify-between p-3 rounded-lg border bg-card">
                  <label className="text-sm font-medium flex items-center gap-2 cursor-pointer" htmlFor="risk-warnings">
                  <AlertTriangle className="w-4 h-4 text-primary" />
                  Include Risk Warnings
                </label>
                <Switch 
                  id="risk-warnings"
                  checked={includeRiskWarnings} 
                  onCheckedChange={setIncludeRiskWarnings}
                />
                </div>

                <div className="flex items-center justify-between p-3 rounded-lg border bg-card">
                  <label className="text-sm font-medium flex items-center gap-2 cursor-pointer" htmlFor="table-of-contents">
                  <List className="w-4 h-4 text-primary" />
                  Include Table of Contents
                </label>
                <Switch 
                  id="table-of-contents"
                  checked={includeTableOfContents} 
                  onCheckedChange={setIncludeTableOfContents}
                />
                </div>

                <div className="flex items-center justify-between p-3 rounded-lg border bg-card">
                  <label className="text-sm font-medium flex items-center gap-2 cursor-pointer" htmlFor="faq">
                  <HelpCircle className="w-4 h-4 text-primary" />
                  Include FAQ Section
                </label>
                <Switch 
                  id="faq"
                  checked={includeFAQ} 
                  onCheckedChange={setIncludeFAQ}
                  />
                </div>
              </div>
            </div>
            
            <Button onClick={generateArticle} disabled={loading} className="w-full bg-primary hover:bg-primary/90">
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Writing Article...
                </>
              ) : (
                <>
                  <PenTool className="w-4 h-4 mr-2" />
                  Generate Article ({wordCount} words)
                </>
              )}
            </Button>
          </div>
        )}

        {/* Step 5: Generated Framework */}
        {step === 5 && contentType === 'framework' && framework && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-brand-teal">Article Framework</h3>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setStep(3)} className="border-brand-teal/30 hover:bg-brand-teal/10">
                  Back
                </Button>
                <Button variant="outline" size="sm" onClick={resetBuilder} className="border-brand-teal/30 hover:bg-brand-teal/10">
                  Start Over
                </Button>
              </div>
            </div>
            
            <div className="bg-brand-teal/5 p-4 rounded-lg max-h-96 overflow-y-auto space-y-4 border border-brand-teal/20">
              {framework?.sections?.map((section, index) => (
                <div key={index} className="border-l-4 border-brand-teal pl-4 bg-white/50 p-3 rounded-r">
                  <h4 className="font-semibold text-lg mb-2 text-brand-teal">{section.heading}</h4>
                  {section.subheadings && (
                    <div className="mb-3">
                      <p className="text-sm font-medium text-muted-foreground mb-1">Subheadings:</p>
                      <ul className="text-sm space-y-1">
                        {section.subheadings.map((sub, subIndex) => (
                          <li key={subIndex} className="ml-4">• {sub}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="mb-3">
                    <p className="text-sm font-medium text-muted-foreground mb-1">Key Points:</p>
                    <ul className="text-sm space-y-1">
                      {section.keyPoints.map((point, pointIndex) => (
                        <li key={pointIndex} className="ml-4">• {point}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">Paragraph Topics:</p>
                    <ul className="text-sm space-y-1">
                      {section.paragraphTopics.map((topic, topicIndex) => (
                        <li key={topicIndex} className="ml-4">• {topic}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
              
              {framework?.keyTakeaways?.length > 0 && (
                <div className="border-l-4 border-green-500 pl-4">
                  <h4 className="font-semibold text-lg mb-2">Key Takeaways</h4>
                  <ul className="text-sm space-y-1">
                    {framework.keyTakeaways?.map((takeaway, index) => (
                      <li key={index} className="ml-4">• {takeaway}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {framework?.callToAction?.length > 0 && (
                <div className="border-l-4 border-blue-500 pl-4">
                  <h4 className="font-semibold text-lg mb-2">Call-to-Action Ideas</h4>
                  <ul className="text-sm space-y-1">
                    {framework.callToAction?.map((cta, index) => (
                      <li key={index} className="ml-4">• {cta}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {framework?.riskWarnings?.length > 0 && (
                <div className="border-l-4 border-red-500 pl-4">
                  <h4 className="font-semibold text-lg mb-2">Risk Warnings</h4>
                  <ul className="text-sm space-y-1">
                    {framework.riskWarnings?.map((warning, index) => (
                      <li key={index} className="ml-4">• {warning}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            
            <Button onClick={() => useGeneratedContent()} className="w-full">
              <Check className="w-4 h-4 mr-2" />
              Use This Framework
            </Button>
          </div>
        )}

        {/* Step 6: Generated Article */}
        {step === 6 && contentType === 'article' && generatedContent && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-primary">Article Generated Successfully!</h3>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setStep(4)}>
                  Back
                </Button>
                <Button variant="outline" size="sm" onClick={resetBuilder}>
                  Start Over
                </Button>
              </div>
            </div>
            
            {/* Show article stats instead of rendering full HTML to prevent crashes */}
            {/* Article Stats */}
            <div className="bg-primary/5 p-6 rounded-lg border border-primary/20 space-y-3">
              <div className="flex items-center gap-2 text-primary">
                <Check className="w-5 h-5" />
                <span className="font-semibold">Article Generated Successfully!</span>
              </div>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Words:</span>
                  <span className="ml-2 font-semibold">
                    {generatedContent.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().split(/\s+/).filter(w => w.length > 0).length}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Characters:</span>
                  <span className="ml-2 font-semibold">{generatedContent.length}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Size:</span>
                  <span className="ml-2 font-semibold">{Math.round(generatedContent.length / 1000)}KB</span>
                </div>
              </div>
            </div>

            {/* Article Preview with smart truncation */}
            <div className="space-y-2">
              <h4 className="font-semibold text-sm">Preview</h4>
              <div className="max-h-96 overflow-y-auto border rounded-lg p-4">
                <SanitizedContent 
                  content={linkedContent || generatedContent}
                  className="prose prose-sm max-w-none [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mb-4 [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mb-3 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mb-2 [&_p]:mb-3 [&_p]:leading-relaxed [&_ul]:mb-3 [&_ul]:pl-6 [&_li]:mb-1"
                />
              </div>
            </div>

            {/* Link Preview Section */}
            {!showLinkPreview && !linkedContent && (
              <div className="border rounded-lg p-4 bg-muted/20">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="font-semibold">Add Internal & External Links</h4>
                    <p className="text-sm text-muted-foreground">Automatically link keywords to relevant articles</p>
                  </div>
                </div>
                <Button 
                  onClick={handleAnalyzeLinks} 
                  disabled={linkingInProgress}
                  variant="outline"
                  className="w-full"
                >
                  {linkingInProgress ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Link2 className="w-4 h-4 mr-2" />
                      Preview & Add Links
                    </>
                  )}
                </Button>
              </div>
            )}

            {/* Link Preview Data */}
            {showLinkPreview && linkPreviewData && !linkedContent && (
              <div className="border rounded-lg p-4 space-y-4 bg-muted/20">
                <h4 className="font-semibold">Link Preview</h4>
                
                {/* Stats */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-primary/5 rounded-lg p-3">
                    <div className="text-xl font-bold text-primary">{linkPreviewData.stats?.totalLinksWillCreate || 0}</div>
                    <div className="text-xs text-muted-foreground">Total Links</div>
                  </div>
                  <div className="bg-blue-500/5 rounded-lg p-3">
                    <div className="text-xl font-bold text-blue-600">{linkPreviewData.stats?.internalLinksCount || 0}</div>
                    <div className="text-xs text-muted-foreground">Internal</div>
                  </div>
                  <div className="bg-green-500/5 rounded-lg p-3">
                    <div className="text-xl font-bold text-green-600">{linkPreviewData.stats?.externalLinksCount || 0}</div>
                    <div className="text-xs text-muted-foreground">External</div>
                  </div>
                </div>

                {/* Internal Links */}
                {linkPreviewData.internalLinks?.length > 0 && (
                  <div className="space-y-2">
                    <h5 className="text-sm font-semibold">Internal Links ({linkPreviewData.internalLinks.length})</h5>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {linkPreviewData.internalLinks.map((link: any, index: number) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-blue-500/5 rounded border border-blue-200/20 text-sm">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">{link.keyword}</div>
                            <div className="text-xs text-muted-foreground truncate">→ /blog/{link.articleSlug}</div>
                          </div>
                          <div className="text-xs bg-blue-600 text-white px-2 py-1 rounded ml-2">
                            {link.occurrences}x
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* External Links */}
                {linkPreviewData.externalLinks?.length > 0 && (
                  <div className="space-y-2">
                    <h5 className="text-sm font-semibold">External Links ({linkPreviewData.externalLinks.length})</h5>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {linkPreviewData.externalLinks.map((link: any, index: number) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-green-500/5 rounded border border-green-200/20 text-sm">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">{link.keyword}</div>
                            <div className="text-xs text-muted-foreground truncate">→ {link.url}</div>
                          </div>
                          <div className="text-xs bg-green-600 text-white px-2 py-1 rounded ml-2">
                            {link.occurrences}x
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex gap-2">
                  <Button 
                    onClick={handleApplyLinks} 
                    disabled={linkingInProgress}
                    className="flex-1"
                  >
                    {linkingInProgress ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Adding Links...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Add {linkPreviewData.stats?.totalLinksWillCreate || 0} Links
                      </>
                    )}
                  </Button>
                  <Button 
                    onClick={handleSkipLinks} 
                    variant="outline"
                    disabled={linkingInProgress}
                  >
                    Skip Links
                  </Button>
                </div>
              </div>
            )}

            {/* Links Applied Confirmation */}
            {linkedContent && (
              <div className="border rounded-lg p-4 bg-green-500/5 border-green-200/20">
                <div className="flex items-center gap-2 text-green-600">
                  <Check className="w-5 h-5" />
                  <span className="font-semibold">Links added successfully!</span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Your content now includes internal and external links
                </p>
              </div>
            )}
            
            <div className="flex gap-4">
              <Button onClick={useGeneratedContent} disabled={loading} className="flex-1">
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Loading into Editor...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Use This Article
                  </>
                )}
              </Button>
              <Button 
                variant="outline" 
                onClick={() => generateArticle()}
                disabled={loading}
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <PenTool className="w-4 h-4 mr-2" />
                )}
                Regenerate
              </Button>
            </div>
          </div>
        )}

        {/* Step 7: Creating Article (Loading State) */}
        {step === 7 && loading && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center space-y-4">
              <Loader2 className="w-12 h-12 animate-spin mx-auto text-primary" />
              <div>
                <p className="text-lg font-semibold text-primary">Creating Your Article...</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Generating SEO metadata and linking keywords to existing articles
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Loading state for framework generation */}
        {loading && contentType === 'framework' && (
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2 text-primary" />
              <p className="text-muted-foreground">Generating framework...</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};